import React, { useCallback, useEffect, useState } from 'react';

import SelectScheduledTransactionDialog from 'components/Dialogs/SelectScheduledTransactionDialog';
import { withDrawerWidth, type RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';

import type { TransactionEditViewModel } from '../TransactionEditViewModel';
import { EditorDrawer, OptionsDrawerLayer, ProgressDrawer, ReviewDrawer } from './WorkflowDrawers';

type Props = {
  rootModalDrawer: RootModalDrawerOptions;
  viewModel: TransactionEditViewModel;
};

const RECURRING_COMPLETION_AUTO_CLOSE_DELAY = 1200;

export default function WorkflowDrawerComponents({ rootModalDrawer, viewModel }: Props): React.JSX.Element {
  const {
    actions: {
      closeAll,
      closeRecurring,
      createRecurring,
      dismissReview,
      doOneTimeBill,
      exitRecurringCreate,
      exitRecurringSelect,
      linkRecurring,
      onEditSeriesFromReview,
      saveRecurring,
      startRecurringSelect,
      stopRecurring,
      updateRecurring,
    },
    derived: {
      completionText,
      currentTxn,
      editSeriesAssignSplits,
      editSeriesInitialTab,
      editSeries,
      isIncome,
      isTransfer,
      loadingLine,
      model,
      showConfirmation,
      showEditRecurring,
      showRecurringOptions,
      showRecurringSelect,
      title,
      waiting,
      waitingForTransfer,
      workflowView,
    },
  } = viewModel;

  // The base details drawer is intentionally narrow ('Small'), but the recurring/create-series
  // sub-flows launched from it need the roomier width they had before this branch. Force 'Large'
  // for those so the details width change stays scoped to the details view itself.
  const subFlowRootModalDrawer = withDrawerWidth(rootModalDrawer, 'Large') ?? rootModalDrawer;
  // The progress overlay is shared: the "Finding transfer" spinner (go-to-other-side) stays at the
  // narrow base width, while the recurring create/assign spinners use the roomier sub-flow width.
  const loadingRootModalDrawer = waitingForTransfer ? rootModalDrawer : subFlowRootModalDrawer;

  const activeWorkflowView =
    workflowView ??
    (waiting || completionText
      ? 'progress'
      : showConfirmation
        ? 'review'
        : editSeries
          ? 'edit-series'
          : showEditRecurring
            ? 'recurring-create'
            : showRecurringSelect
              ? 'recurring-select'
              : 'transaction');

  const showEditRecurringOverlayDrawer = Boolean(model && activeWorkflowView === 'recurring-create' && !waiting);
  const [hasRenderedEditRecurringOverlayDrawer, setHasRenderedEditRecurringOverlayDrawer] = useState(false);

  // Derived state: set synchronously during render so the EditorDrawer mounts in
  // the same commit that showEditRecurringOverlayDrawer first becomes true, avoiding
  // the extra paint cycle that a useEffect would introduce.
  if (showEditRecurringOverlayDrawer && !hasRenderedEditRecurringOverlayDrawer) {
    setHasRenderedEditRecurringOverlayDrawer(true);
  }

  useEffect(() => {
    if (!completionText) return undefined;
    const timeoutId = window.setTimeout(closeAll, RECURRING_COMPLETION_AUTO_CLOSE_DELAY);
    return () => window.clearTimeout(timeoutId);
  }, [closeAll, completionText]);

  const shouldRenderEditRecurringOverlayDrawer = Boolean(
    model && (showEditRecurringOverlayDrawer || hasRenderedEditRecurringOverlayDrawer),
  );

  // Stable reference: prevents StandardDrawer's overlay exit timer from being
  // cancelled and restarted if the parent re-renders mid-animation.
  const handleEditorExited = useCallback(() => setHasRenderedEditRecurringOverlayDrawer(false), []);

  return (
    <>
      <OptionsDrawerLayer
        isTransfer={isTransfer}
        isIncome={isIncome}
        createRecurring={createRecurring}
        doOneTimeBill={doOneTimeBill}
        onClose={stopRecurring}
        rootModalDrawer={subFlowRootModalDrawer}
        showConfirmation={showConfirmation}
        showEditRecurring={showEditRecurring}
        showRecurringOptions={showRecurringOptions}
        showRecurringSelect={showRecurringSelect}
        startRecurringSelect={startRecurringSelect}
        title={title}
        waiting={waiting}
      />

      {model && shouldRenderEditRecurringOverlayDrawer && (
        <EditorDrawer
          fakeCreate
          open={showEditRecurringOverlayDrawer}
          rootModalDrawer={subFlowRootModalDrawer}
          scheduledTransaction={model}
          onSubmit={saveRecurring}
          onClose={closeAll}
          onBack={exitRecurringCreate}
          onExited={handleEditorExited}
          title='Create series'
        />
      )}

      {activeWorkflowView === 'review' && (
        <ReviewDrawer
          open
          model={model}
          onClose={dismissReview}
          edit={onEditSeriesFromReview}
          rootModalDrawer={subFlowRootModalDrawer}
        />
      )}

      {activeWorkflowView === 'edit-series' && model && (
        <EditorDrawer
          open
          key={model.clientId || model.id}
          rootModalDrawer={subFlowRootModalDrawer}
          scheduledTransaction={model}
          onClose={closeRecurring}
          onSubmit={updateRecurring}
          initialTab={editSeriesInitialTab}
          initialShowAssignSplits={editSeriesAssignSplits}
        />
      )}

      {activeWorkflowView === 'recurring-select' && (
        <SelectScheduledTransactionDialog
          open={!waiting}
          onClose={closeAll}
          onSelected={linkRecurring}
          onCreate={createRecurring}
          rootModalDrawer={subFlowRootModalDrawer}
          showBackButton
          onBack={exitRecurringSelect}
          txn={currentTxn}
        />
      )}

      {activeWorkflowView === 'progress' && (
        <ProgressDrawer
          open
          loadingString={loadingLine}
          rootModalDrawer={loadingRootModalDrawer}
          onClose={closeAll}
          successString={completionText}
        />
      )}
    </>
  );
}
